import classNames from "classnames";
import React, { useCallback } from "react";
import { ProvetDayModel } from "../../types/calendar";

interface CalendarGridProps {
  weeks: ProvetDayModel[][];
  selectedDate: Date;
  selectedVetId: number;
  onDateChange: (date: Date) => void;
}

export const CalendarGrid = ({
  weeks,
  selectedDate,
  selectedVetId,
  onDateChange
}: CalendarGridProps) => {
  // Helper function to get day classes
  const getDayClasses = useCallback(
    (day: ProvetDayModel): string => {
      const dayDate = new Date(day.date);
      const isToday = dayDate.toDateString() === new Date().toDateString();
      const isSelected = dayDate.toDateString() === selectedDate.toDateString();
      const hasAppointments = (day.availableAppointments?.length || 0) > 0;

      // Matching the Blazor condition exactly:
      // (day.AvailableAppointments?.Count == 0 && (day.Date.Month < _selectedDate.Month || day.Date.Month > _selectedDate.Month))
      const isOtherMonth =
        day.availableAppointments?.length === 0 &&
        (dayDate.getMonth() < selectedDate.getMonth() ||
          dayDate.getMonth() > selectedDate.getMonth());

      const isNotSelectable = dayDate < new Date();

      // Check if has appointments for selected vet
      const hasAppointmentsForSelectedVet =
        selectedVetId > 0 &&
        day.availableAppointments?.some(d => d.veterinarianId === selectedVetId);

      // Determine if should show appointments available styling
      const showAppointmentsAvailable =
        hasAppointments &&
        !isSelected &&
        (selectedVetId === 0 || hasAppointmentsForSelectedVet);

      return classNames(
        // Base classes
        "w-[2.5rem] h-[2.5rem] sm:w-[4.85rem] sm:h-[3.75rem] md:w-[4.85rem] md:h-[3.75rem]",
        "flex-grow flex-shrink-0 basis-0",
        "font-guardian text-t-sm font-normal text-center",
        "rounded-tl-br-sm transition-short-in-out",
        "focus-outline",
        {
          // Today border
          "border-2 border-green-600": isToday,

          // Selected day styling (highest priority)
          "text-white bg-dark-blue": isSelected,
          "hover:text-white hover:bg-dark-blue": isSelected,
          "focus-visible:text-white focus-visible:bg-dark-blue": isSelected,

          // Has appointments styling (when not selected)
          "text-dark-blue bg-green-400": showAppointmentsAvailable,

          // Not selectable styling (past dates)
          "text-neutral-200 bg-white cursor-default": isNotSelectable && !isSelected,
          "hover:text-neutral-200 hover:bg-white": isNotSelectable && !isSelected,
          "focus-visible:text-neutral-200 focus-visible:bg-white":
            isNotSelectable && !isSelected,

          // Other month styling (no appointments in different month)
          "text-t-neutral-400 bg-white": isOtherMonth && !isSelected && !isNotSelectable,

          // Default styling (when none of the above apply)
          "cursor-pointer": !isNotSelectable || isSelected || showAppointmentsAvailable,
          "text-t-light-blue hover:text-neutral-300 bg-neutral-50 hover:bg-neutral-200":
            !isSelected &&
            !showAppointmentsAvailable &&
            !isNotSelectable &&
            !isOtherMonth,
          "focus-visible:text-neutral-300 focus-visible:bg-neutral-50":
            !isSelected && !showAppointmentsAvailable && !isNotSelectable && !isOtherMonth
        }
      );
    },
    [selectedDate, selectedVetId]
  );

  return (
    <div
      className="flex flex-col gap-xs sm:gap-md border-t border-neutral-100 pt-sm sm:pt-md"
      data-online-booking-calendar
    >
      {weeks.map((week, weekIndex) => (
        <div
          key={weekIndex}
          className="flex flex-row items-start gap-xs sm:gap-md justify-between"
          data-online-booking-calendar-week
        >
          {week.map((day, dayIndex) => {
            const dayDate = new Date(day.date);
            const dayClasses = getDayClasses(day);
            const hasAppointments = (day.availableAppointments?.length || 0) > 0;
            const canSelect = dayDate >= new Date() || hasAppointments;
            return (
              <button
                key={dayIndex}
                onClick={canSelect ? () => onDateChange(dayDate) : undefined}
                disabled={!canSelect}
                className={dayClasses}
                data-online-booking-calendar-date={dayDate.getDate()}
                data-online-booking-calendar-date-available={
                  hasAppointments ? "true" : "false"
                }
                data-online-booking-calendar-date-selected={
                  dayDate.toDateString() === selectedDate.toDateString()
                    ? "true"
                    : undefined
                }
              >
                {dayDate.getDate().toString().padStart(2, "0")}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
